import { useEffect, useLayoutEffect } from "react";
import { useStore } from "ra-core";

import { ThemeProviderContext, type Theme } from "./theme-context";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

// Helper to get the initial theme before React hydrates
const getInitialTheme = (defaultTheme: Theme): Theme => {
  if (typeof window === "undefined") return defaultTheme;

  // Try to read from common ra-core store prefixes in localStorage
  // ra-core localStorageStore with appKey "CRM" uses "RaStoreCRM." prefix
  // default ra-core uses "RaStore." prefix
  const possibleKeys = [
    "RaStoreCRM.theme",
    "RaStore.theme",
    "CRM.theme",
    "theme",
  ];

  for (const key of possibleKeys) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored) as Theme;
      }
    } catch {
      // Ignore parse errors
    }
  }
  return defaultTheme;
};

// Apply theme class immediately to avoid flash
const applyThemeClass = (theme: Theme) => {
  if (typeof window === "undefined") return;

  const root = window.document.documentElement;
  root.classList.remove("light", "dark");

  if (theme === "system") {
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
      .matches
      ? "dark"
      : "light";
    root.classList.add(systemTheme);
  } else {
    root.classList.add(theme);
  }
};

/**
 * Theme provider that enables light, dark, and system theme modes.
 *
 * @internal
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  ...props
}: ThemeProviderProps) {
  // Apply theme immediately on mount to prevent flash
  useLayoutEffect(() => {
    const initialTheme = getInitialTheme(defaultTheme);
    applyThemeClass(initialTheme);
  }, [defaultTheme]);

  const [theme, setTheme] = useStore<Theme>(storageKey, defaultTheme);

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const value = {
    theme,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
